"use strict";
/**
 * Copyright © 2020 Johnson & Johnson
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

var __importDefault = this && this.__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.withUnwrap = void 0;

var react_1 = __importDefault(require("react"));

var lodash_1 = require("lodash");

var uuid_1 = require("uuid");

var core_1 = require("@bodiless/core");

var withChameleonContext_1 = require("./withChameleonContext");

var useToggleButtonMenuOption = function useToggleButtonMenuOption() {
  var _a = withChameleonContext_1.useChameleonContext(),
      isOn = _a.isOn,
      selectableComponents = _a.selectableComponents,
      setActiveComponent = _a.setActiveComponent;

  var newKey = isOn ? null : Object.keys(selectableComponents).find(function (key) {
    return key !== withChameleonContext_1.DEFAULT_KEY;
  }) || null;
  return {
    label: 'Toggle',
    icon: isOn ? 'toggle_off' : 'toggle_on',
    handler: function handler() {
      return setActiveComponent(newKey);
    }
  };
};

var useSwapButtonMenuOption = function useSwapButtonMenuOption() {
  var _a = withChameleonContext_1.useChameleonContext(),
      selectableComponents = _a.selectableComponents,
      activeComponent = _a.activeComponent,
      setActiveComponent = _a.setActiveComponent;

  var renderForm = function renderForm() {
    var _a = core_1.useMenuOptionUI(),
        ComponentFormLabel = _a.ComponentFormLabel,
        ComponentFormRadioGroup = _a.ComponentFormRadioGroup,
        ComponentFormRadio = _a.ComponentFormRadio;

    var radios = Object.getOwnPropertyNames(selectableComponents).map(function (name) {
      return react_1.default.createElement(ComponentFormLabel, {
        id: "bl-component-form-chameleon-radio-" + name,
        key: name
      }, react_1.default.createElement(ComponentFormRadio, {
        value: name
      }), selectableComponents[name].title || name);
    });
    return react_1.default.createElement(react_1.default.Fragment, null, react_1.default.createElement(ComponentFormRadioGroup, {
      field: "component"
    }, radios));
  };

  var render = core_1.useContextMenuForm({
    initialValues: {
      component: activeComponent === withChameleonContext_1.DEFAULT_KEY ? Object.keys(selectableComponents)[0] : activeComponent
    },
    submitValues: function submitValues(d) {
      return setActiveComponent(d.component || null);
    },
    renderForm: renderForm
  });
  return {
    icon: 'repeat',
    label: 'Swap',
    handler: function handler() {
      return render;
    },
    formTitle: 'Choose a component'
  };
};

exports.withUnwrap = function (Component) {
  var WithUnwrapChameleon = function WithUnwrapChameleon(props) {
    var _a = withChameleonContext_1.useChameleonContext(),
        isOn = _a.isOn,
        setActiveComponent = _a.setActiveComponent;

    if (!isOn) return react_1.default.createElement(Component, __assign({}, props));

    var unwrap = function unwrap() {
      return setActiveComponent(null);
    };

    return react_1.default.createElement(Component, __assign({}, props, {
      unwrap: unwrap
    }));
  };

  return WithUnwrapChameleon;
};
/**
 * Adds a menu button which controls the state of the chameleon.
 *
 * If the chameleon has more than one element in it's design, this will show a form allowing
 * the user to choose which to apply.  Otherwise, this will be a toggle button.
 *
 * @param nodeKeys Location of the chameleon state data
 * @param defaultData Default chameleon state data.
 * @param useOverrides Menu option overrides.
 *
 * @return HOC which adds the menu button.
 */


var withChameleonButton = function withChameleonButton(useOverrides) {
  if (useOverrides === void 0) {
    useOverrides = function useOverrides() {
      return {};
    };
  }

  var useMenuOptions = function useMenuOptions(props) {
    var overrides = useOverrides(props); // if useOverrides returns falsy, it means not to provide the button.

    if (!overrides) return [];
    var selectableComponents = withChameleonContext_1.useChameleonContext().selectableComponents;
    var extMenuOptions = Object.keys(selectableComponents).length > 1 ? useSwapButtonMenuOption : useToggleButtonMenuOption;

    var baseDefinition = __assign(__assign({
      name: "chameleon-" + uuid_1.v1(),
      global: false,
      local: true
    }, extMenuOptions()), overrides);

    return core_1.createMenuOptionGroup(baseDefinition);
  };

  return lodash_1.flowRight(core_1.withMenuOptions({
    useMenuOptions: useMenuOptions,
    name: 'Chamelion'
  }), core_1.withContextActivator('onClick'), core_1.withLocalContextMenu);
};

exports.default = withChameleonButton;